import React, { useState } from "react";
import { Info } from "lucide-react";

const defects = [
  { type: "Crack", info: "A crack in weld, often critical if large." },
  { type: "Porosity", info: "Gas pockets trapped inside weld metal." },
  { type: "Undercut", info: "Groove along weld toe reducing thickness." },
  { type: "Incomplete Fusion", info: "Weld metal fails to fuse completely." },
];

export default function WeldDefectChecker() {
  const [defectType, setDefectType] = useState("");
  const [size, setSize] = useState("");
  const [result, setResult] = useState(null);

  const checkDefect = () => {
    if (!defectType || !size) return;

    let severity = "Unknown";
    const s = parseFloat(size);

    switch (defectType) {
      case "Crack":
        severity = s > 5 ? "Critical - Repair Immediately" : "Moderate - Monitor";
        break;
      case "Porosity":
        severity = s > 3 ? "Moderate - Consider Repair" : "Minor - Acceptable";
        break;
      case "Undercut":
        severity = s > 2 ? "Moderate - Repair Recommended" : "Minor - Acceptable";
        break;
      case "Incomplete Fusion":
        severity = s > 4 ? "Critical - Repair Immediately" : "Moderate - Monitor";
        break;
      default:
        severity = "Unknown";
    }

    setResult(severity);
  };

  const downloadResult = () => {
    const content = `Weld Defect Check
------------------------
Defect Type: ${defectType}
Size: ${size} mm
Evaluation: ${result}
Standards Reference: ASME Section IX, AWS D1.1, ASNT SNT-TC-1A`;
    const element = document.createElement("a");
    const file = new Blob([content], { type: "text/plain" });
    element.href = URL.createObjectURL(file);
    element.download = "weld-defect.txt";
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const getSeverityColor = () => {
    if (!result) return "bg-gray-100 text-cyan-700";
    if (result.includes("Critical")) return "bg-red-100 text-red-700";
    if (result.includes("Moderate")) return "bg-orange-100 text-orange-700";
    if (result.includes("Minor")) return "bg-green-100 text-green-700";
    return "bg-gray-100 text-cyan-700";
  };

  return (
    <div className="bg-gray-50 min-h-screen py-12 px-6">
      <div className="max-w-2xl mx-auto bg-white p-8 rounded-2xl shadow-lg">
        {/* Header */}
        <h2 className="text-3xl font-bold text-center text-cyan-700 mb-2">
          Weld Defect Checker
        </h2>
        <p className="text-center text-gray-600 mb-6">
          Quickly evaluate weld defects based on type and size.  
          Evaluations follow <strong>ASME Section IX</strong>, <strong>AWS D1.1</strong>, and <strong>ASNT SNT-TC-1A</strong>.
        </p>

        {/* Inputs */}
<div className="space-y-4">
  {/* Defect Type */}
  <div>
    <label className="font-medium flex items-center gap-2 mb-1">
      Defect Type
      <Info
        className="w-4 h-4 text-gray-400"
        title="Select the weld defect type to evaluate (ASNT / AWS / ASME references)"
      />
    </label>
    <select
      value={defectType}
      onChange={(e) => {
        setDefectType(e.target.value);
        setSize(""); // reset input when defect changes
        setResult(null);
      }}
      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-cyan-500 focus:border-cyan-500"
    >
      <option value="">Select Defect</option>
      {defects.map((d) => (
        <option key={d.type} value={d.type}>
          {d.type}
        </option>
      ))}
    </select>
  </div>

  {/* Defect Measurement */}
  <div>
    <label className="font-medium mb-1">
      {defectType === "Crack" ? "Crack Length (mm)" :
       defectType === "Porosity" ? "Pore Diameter (mm)" :
       defectType === "Undercut" ? "Undercut Depth (mm)" :
       defectType === "Incomplete Fusion" ? "Fusion Length (mm)" :
       "Defect Size (mm)"}
    </label>
    <input
      type="number"
      value={size}
      placeholder={
        defectType === "Crack" ? "e.g. 25 (length in mm)" :
        defectType === "Porosity" ? "e.g. 2 (diameter in mm)" :
        defectType === "Undercut" ? "e.g. 1.5 (depth in mm)" :
        defectType === "Incomplete Fusion" ? "e.g. 5 (length in mm)" :
        "e.g. 3.5"
      }
      onChange={(e) => {
        setSize(e.target.value);
        setResult(null);
      }}
      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-cyan-500 focus:border-cyan-500"
    />
    {defectType && (
      <p className="text-xs text-gray-500 mt-1">
        {defectType === "Crack" && "Measure the linear crack length (mm)."}
        {defectType === "Porosity" && "Measure the diameter of the largest pore (mm)."}
        {defectType === "Undercut" && "Measure the depth of the undercut along weld toe (mm)."}
        {defectType === "Incomplete Fusion" && "Measure the length of unfused area along weld (mm)."}
      </p>
    )}
  </div>

  {/* Buttons */}
  <div className="text-center flex justify-center gap-4 mt-4">
    <button
      onClick={checkDefect}
      className="px-6 py-2 bg-cyan-700 text-white font-semibold rounded-full hover:bg-cyan-600 transition"
    >
      Check Defect
    </button>
    {result && (
      <button
        onClick={downloadResult}
        className="px-6 py-2 bg-green-600 text-white font-semibold rounded-full hover:bg-green-500 transition"
      >
        Download Result
      </button>
    )}
  </div>

  {/* Result */}
  {result && (
    <div
      className={`mt-6 text-center p-4 rounded-lg font-semibold ${getSeverityColor()}`}
    >
      Evaluation: {result}
      <p className="text-xs text-gray-600 mt-1">
        Based on ASME Section IX, AWS D1.1, ASNT SNT-TC-1A
      </p>
    </div>
  )}
</div>


        {/* Standards Table */}
        <div className="mt-8 p-4 bg-gray-50 rounded-lg border border-gray-200 text-sm text-gray-700">
          <h4 className="font-bold text-gray-900 mb-2">Standards Reference</h4>
          <table className="w-full text-left border-collapse">
            <thead>
              <tr>
                <th className="border-b border-gray-300 p-2">Defect</th>
                <th className="border-b border-gray-300 p-2">Reference Standard</th>
                <th className="border-b border-gray-300 p-2">Acceptable Size (mm)</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="border-b border-gray-200 p-2">Crack</td>
                <td className="border-b border-gray-200 p-2">ASME Section IX, ASNT SNT-TC-1A</td>
                <td className="border-b border-gray-200 p-2">0 mm (Repair)</td>
              </tr>
              <tr>
                <td className="border-b border-gray-200 p-2">Porosity</td>
                <td className="border-b border-gray-200 p-2">AWS D1.1, ASNT SNT-TC-1A</td>
                <td className="border-b border-gray-200 p-2">&lt; 3 mm minor</td>
              </tr>
              <tr>
                <td className="border-b border-gray-200 p-2">Undercut</td>
                <td className="border-b border-gray-200 p-2">ASME Section IX, ASNT SNT-TC-1A</td>
                <td className="border-b border-gray-200 p-2">&lt; 2 mm minor</td>
              </tr>
              <tr>
                <td className="p-2">Incomplete Fusion</td>
                <td className="p-2">ASME Section IX, ASNT SNT-TC-1A</td>
                <td className="p-2">&lt; 4 mm moderate</td>
              </tr>
            </tbody>
          </table>
          <p className="mt-2 text-gray-500 text-xs">
            Note: This tool is intended for general guidance. Always follow official codes and standards for inspection and repair.
          </p>
        </div>

        {/* Footer */}
        {/* <p className="text-xs text-center text-gray-400 mt-10">
          © {new Date().getFullYear()} omkarmore.com — Free NDT Tools
        </p> */}
      </div>
    </div>
  );
}
